<template>
  <section>
    <template v-if="enableNewRequisites">
      <div
        v-if="!visibleFreeformRequisites && !visibleAdvancedRequisites.length && !visibleSimpleRequisites.length"
        class="mb-2 text-base md:text-lg color-light-black"
      >
        <span v-if="emptyValuePlaceholder">{{emptyValuePlaceholder}}</span>
        <span v-else>No {{question.label || 'Requisites'}}</span>
      </div>
      <template v-else>
        <RequisitesFreeform
          v-if="visibleFreeformRequisites && visibleFreeformRequisites.value"
          class="mb-4"
          :value="visibleFreeformRequisites"
          :question="getNestedQuestion('requisitesFreeform')"
        />
        <RequisitesAdvance
          v-if="visibleAdvancedRequisites && visibleAdvancedRequisites.length"
          :value="visibleAdvancedRequisites"
          class="mb-4"
        />
        <RequisitesSimple
          v-if="visibleSimpleRequisites && visibleSimpleRequisites.length"
          :value="visibleSimpleRequisites"
          :document-value="documentValue"
          :entity-type="entityType"
          @showModal="showModal"
        />
      </template>
    </template>
    <template v-else>
      <div
        v-if="!visibleFreeformRequisites && !visibleAdvancedRequisites.length && !visibleSimpleRequisites.length"
        class="text-center text-gray-500 bg-gray-200 rounded p-3 mb-2 border"
      >
        <span v-if="emptyValuePlaceholder">{{emptyValuePlaceholder}}</span>
        <span v-else>No {{question.label || 'Requisites'}}</span>
      </div>
      <template v-else>
        <RequisitesFreeformLegacy
          v-if="visibleFreeformRequisites && visibleFreeformRequisites.value"
          class="mb-2"
          :value="visibleFreeformRequisites"
          :question="getNestedQuestion('requisitesFreeform')"
        />
        <RequisitesAdvanceLegacy
          v-if="visibleAdvancedRequisites && visibleAdvancedRequisites.length"
          :value="visibleAdvancedRequisites"
          class="mb-2"
        />
        <RequisitesSimpleLegacy
          v-if="visibleSimpleRequisites && visibleSimpleRequisites.length"
          :value="visibleSimpleRequisites"
          :document-value="documentValue"
          :entity-type="entityType"
          @showModal="showModal"
        />
      </template>
    </template>
    <CourseSetModal
      :course-set="selectedCourseSet"
      :course-list="courseList"
      @close="closeCourseSetModal"
    />
    <RequisiteSetModal
      :requisite-set="selectedRequisiteSet"
      @close="closeRequisiteSetModal"
    />
    <ItemDetailsModal
      :details="itemDetails"
      @close="itemDetails = false"
    >
      <template v-if="itemDetails.course">
        <ResourcePreview
          v-if="configurableResourcePreview"
          class="rounded bg-white border border-gray-300 my-4"
          :value="itemDetails.course"
          :template="coursePreviewTemplate"
          entity-type="course"
        />
        <CourseModalCreditsDetails
          v-else-if="showCreditsOnCourseModal"
          :course="itemDetails.course"
          class="mt-4"
        />
      </template>
    </ItemDetailsModal>
  </section>
</template>

<script>
import { provide } from '@vue/composition-api'
import { mapState, mapGetters } from 'vuex'
import RequisitesSimple from './RequisitesSimple/RequisitesSimple'
import RequisitesAdvance from './RequisitesAdvance/RequisitesAdvance'
import RequisitesFreeform from './RequisitesFreeform'
import RequisitesSimpleLegacy from './LegacyRequisites/RequisitesSimple/RequisitesSimple'
import RequisitesAdvanceLegacy from './LegacyRequisites/RequisitesAdvance/RequisitesAdvance'
import RequisitesFreeformLegacy from './LegacyRequisites/RequisitesFreeform'
import CourseSetModal from '@/components/CourseSetModal'
import RequisiteSetModal from '@/components/RequisiteSetModal'
import ItemDetailsModal from '@/components/ItemDetailsModal'
import CourseModalCreditsDetails from '@/components/CourseModalCreditsDetails'
import ResourcePreview from '@/components/ResourcePreview'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { isNestedFieldHidden } from '@/utils/fieldConfig'
import {
  REQUISITES_REDESIGNED,
  CONFIGURABLE_RESOURCE_PREVIEW,
} from '@/constants/featureFlags'

export default {
  components: {
    CourseSetModal,
    RequisiteSetModal,
    RequisitesSimple,
    RequisitesAdvance,
    RequisitesFreeform,
    ItemDetailsModal,
    CourseModalCreditsDetails,
    RequisitesSimpleLegacy,
    RequisitesAdvanceLegacy,
    RequisitesFreeformLegacy,
    ResourcePreview,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    question: {
      type: Object,
      default: () => ({}),
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    entityType: {
      type: String,
      default: '',
    },
  },
  setup() {
    const {
      enableNewRequisites,
      configurableResourcePreview,
    } = useFeatureFlags({
      enableNewRequisites: REQUISITES_REDESIGNED,
      configurableResourcePreview: CONFIGURABLE_RESOURCE_PREVIEW,
    })
    provide('enableNewRequisites', enableNewRequisites)

    return { enableNewRequisites, configurableResourcePreview }
  },
  data() {
    return {
      selectedCourseSet: false,
      selectedRequisiteSet: false,
      isLoading: false,
      courseSetCoursesData: {},
      itemDetails: false,
    }
  },
  computed: {
    ...mapState('settings', ['school', 'coursePreviewTemplate']),
    ...mapGetters('settings', [
      'effectiveDatesRangeAsString',
      'hideCourseModal',
      'hideProgramModal',
      'showCreditsOnCourseModal',
    ]),
    courseList() {
      if (this.isLoading) return []
      if (!this.selectedCourseSet) return []
      const isDynamicCourseSet = this.selectedCourseSet.type === 'dynamic'
      const dynamicCourseList = this.selectedCourseSet.dynamicCourseList?.length && this.selectedCourseSet.dynamicCourseList
      return (
        (isDynamicCourseSet && dynamicCourseList) ||
        this.selectedCourseSet.courseList ||
        []
      )
        .map((courseId) => {
          return this.courseSetCoursesData[courseId]
        })
        .filter(Boolean)
    },
    visibleFreeformRequisites() {
      if (isNestedFieldHidden(this.question, 'requisitesFreeform')) return {}
      return (
        this.value.requisitesFreeform?.showInCatalog &&
        this.value.requisitesFreeform
      )
    },
    visibleAdvancedRequisites() {
      if (isNestedFieldHidden(this.question, 'requisitesAdvanced')) return []
      return (this.value.requisitesAdvanced || []).filter(
        (v) => v.showInCatalog
      )
    },
    visibleSimpleRequisites() {
      if (isNestedFieldHidden(this.question, 'requisitesSimple')) return []
      return (this.value.requisitesSimple || []).filter((v) => v.showInCatalog)
    },
    emptyValuePlaceholder() {
      return this.question?.config?.emptyValuePlaceholder
    },
  },
  methods: {
    getNestedQuestion(questionId) {
      return this.question?.config?.fields?.[questionId]
    },
    showModal({ type, details }) {
      if (type === 'course' || type === 'program') {
        if (!details.id || !details.path) return

        if (this.hideCourseModal && details.path === 'courses') {
          return this.$router.push(`/${details.path}/${encodeURIComponent(details.id)}`)
        }
        if (this.hideProgramModal && details.path === 'programs') {
          return this.$router.push(`/${details.path}/${details.id}`)
        }

        this.itemDetails = details
      } else if (type === 'courseSet') {
        if (!details.courseSet) return
        this.selectedCourseSet = details.courseSet
        if (details.courseSet.courseSetGroupId)
          this.loadCourseSetWithRelatedData(details.courseSet.courseSetGroupId)
      } else if (type === 'requisiteSet') {
        if (!details.requisiteSet) return
        this.selectedRequisiteSet = details.requisiteSet
      }
    },
    closeCourseSetModal() {
      this.selectedCourseSet = false
      this.courseSetCoursesData = {}
    },
    closeRequisiteSetModal() {
      this.selectedRequisiteSet = false
    },
    async loadCourseSetWithRelatedData(courseSetId) {
      this.isLoading = true
      try {
        const data = await this.$axios.$get(
          `/cm/${this.school}/courseSets`,
          {
            params: {
              courseSetGroupIds: [courseSetId],
              includeRelatedData: true,
              effectiveDatesRange: `${this.effectiveDatesRangeAsString}`,
              courseColumns: ['code', 'subjectCode', 'longGame', 'name'],
            },
          }
        )
        if (data.courses) {
          this.courseSetCoursesData = Object.values(data.courses).reduce(
            (coursesById, course) => {
              coursesById[course.courseGroupId] = course
              return coursesById
            },
            {}
          )
        }
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn(e)
      }
      this.isLoading = false
    },
  },
}
</script>
